import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Bell, Package, Clock, CheckCircle, Wallet, Sparkles, Camera, Film, 
  Music, Mic, PenTool, Smartphone, MapPin, IndianRupee, ArrowRight, 
  Calendar, Zap, Users, Gift, Share2, Crown, BadgeCheck, AlertCircle,
  Check, Circle, ChevronRight, TrendingUp, Loader2, XCircle,
  Clapperboard, Gamepad2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BottomNav from "@/components/BottomNav";
import StarsOfTheDaySection from "@/components/StarsOfTheDaySection";
import { useAuth, API } from "@/App";

const TalentHome = () => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [profile, setProfile] = useState(null);
  const [talentProfile, setTalentProfile] = useState(null);
  const [balance, setBalance] = useState({ available: 0, pending: 0, total_earnings: 0 });
  const [stats, setStats] = useState({
    hire_requests: 0,
    active_gigs: 0,
    completed_gigs: 0,
    total_earnings: 0
  });
  const [opportunities, setOpportunities] = useState([]);
  const [referralInfo, setReferralInfo] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Verified Badge Modal State
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyForm, setVerifyForm] = useState({
    instagram_link: "",
    youtube_link: "",
    instagram_followers: "",
    youtube_followers: ""
  });

  // Stars of the Day
  const [starsOfDay, setStarsOfDay] = useState([]);
  const [showBoostModal, setShowBoostModal] = useState(false);

  // Official FoundStar Instagram (used by the "Follow Us" task)
  const FOUNDSTAR_IG_HANDLE = "foundstar_";
  const FOUNDSTAR_IG_URL = `https://instagram.com/${FOUNDSTAR_IG_HANDLE}`;
  
  // Categories config
  const categoryIcons = {
    models: { icon: Camera, emoji: "📸", color: "#FF4D6D" },
    actors: { icon: Film, emoji: "🎬", color: "#7B2FBE" },
    dancers: { icon: Music, emoji: "💃", color: "#FF6B00" },
    voice_artists: { icon: Mic, emoji: "🎙️", color: "#1A73E8" },
    photographers: { icon: Camera, emoji: "📷", color: "#00A86B" },
    editors: { icon: Clapperboard, emoji: "🎞️", color: "#14B8A6" },
    gaming: { icon: Gamepad2, emoji: "🎮", color: "#8B5CF6" },
    content_writers: { icon: PenTool, emoji: "✍️", color: "#FFD700" },
    influencers: { icon: Smartphone, emoji: "📱", color: "#E91E63" }
  };
  
  useEffect(() => {
    fetchAllData();
    
    // Poll for updates
    const interval = setInterval(() => {
      fetchStats();
      fetchNotifications();
    }, 15000);
    
    return () => clearInterval(interval);
  }, []);
  
  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchProfile(),
      fetchTalentProfile(),
      fetchBalance(),
      fetchStats(),
      fetchOpportunities(),
      fetchReferralInfo(),
      fetchNotifications(),
      fetchStarsOfDay()
    ]);
    setLoading(false);
  };
  
  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/influencer/profile`, { headers: getAuthHeaders() });
      setProfile(response.data);
    } catch (error) {
      console.error("Failed to fetch profile");
    }
  };

  const fetchStarsOfDay = async () => {
    try {
      const response = await axios.get(`${API}/stars-of-the-day`);
      setStarsOfDay(response.data?.stars || []);
    } catch (error) {
      // Soft-fail — Stars of the Day section just won't render
      setStarsOfDay([]);
    }
  };

  const handleFollowInstagramTask = async () => {
    // 1. Open FoundStar Instagram in a new tab
    window.open(FOUNDSTAR_IG_URL, "_blank", "noopener,noreferrer");
    // 2. Mark task done (optimistic) and persist server-side
    setProfile(prev => prev ? { ...prev, followed_instagram: true } : prev);
    try {
      await axios.post(`${API}/influencer/tasks/follow-instagram`, {}, { headers: getAuthHeaders() });
      toast.success("🎉 Awesome! Task marked as done");
    } catch (e) {
      // revert
      setProfile(prev => prev ? { ...prev, followed_instagram: false } : prev);
      toast.error("Couldn't save. Please try again.");
    }
  };
  
  const fetchTalentProfile = async () => {
    try {
      const response = await axios.get(`${API}/talent/profile`, { headers: getAuthHeaders() });
      if (response.data.has_profile) {
        setTalentProfile(response.data.profile);
      }
    } catch (error) {
      console.error("Failed to fetch talent profile");
    }
  };
  
  const fetchBalance = async () => {
    try {
      const response = await axios.get(`${API}/influencer/balance`, { headers: getAuthHeaders() });
      setBalance(response.data);
    } catch (error) {
      console.error("Failed to fetch balance");
    }
  };
  
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/talent/bookings/received`, { headers: getAuthHeaders() });
      const bookings = response.data.bookings || [];
      setStats({
        hire_requests: bookings.filter(b => b.status === 'pending').length,
        active_gigs: bookings.filter(b => ['accepted', 'paid'].includes(b.status)).length,
        completed_gigs: bookings.filter(b => b.status === 'completed').length,
        total_earnings: balance.total_earnings || 0
      });
    } catch (error) {
      // Use profile data as fallback
      setStats(prev => ({
        ...prev,
        completed_gigs: profile?.closed_deals || 0,
        total_earnings: balance.total_earnings || 0
      }));
    }
  };
  
  const fetchOpportunities = async () => {
    try {
      // Fetch real opportunities from backend
      const response = await axios.get(`${API}/opportunities`, { headers: getAuthHeaders() });
      setOpportunities(response.data || []);
    } catch (error) {
      console.error("Failed to fetch opportunities");
      // Fallback to empty array
      setOpportunities([]);
    }
  };
  
  const applyToOpportunity = async (opportunityId) => {
    try {
      const response = await axios.post(`${API}/opportunities/apply`, 
        { opportunity_id: opportunityId },
        { headers: getAuthHeaders() }
      );
      toast.success(response.data.message || "Application submitted!");
      fetchOpportunities(); // Refresh to show updated status
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply");
    }
  };
  
  const fetchReferralInfo = async () => {
    try {
      const response = await axios.get(`${API}/referral/info`, { headers: getAuthHeaders() });
      setReferralInfo(response.data);
    } catch (error) {
      console.error("Failed to fetch referral info");
    }
  };
  
  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/influencer/notifications`, { headers: getAuthHeaders() });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error("Failed to fetch notifications");
    }
  };
  
  const formatCurrency = (amount) => {
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}K`;
    return `₹${amount}`;
  };
  
  const getDaysLeft = (deadline) => {
    const days = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };
  
  const shareOnWhatsApp = () => {
    if (referralInfo?.referral_code) {
      const text = `Join FOUNDSTAR and earn money as a creator! Use my referral code: ${referralInfo.referral_code}. Sign up free: ${window.location.origin}/?ref=${referralInfo.referral_code}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };
  
  // Handle Verified Badge Purchase
  const handleGetVerified = () => {
    // Pre-fill with talent profile data if available
    setVerifyForm({
      instagram_link: talentProfile?.instagram_handle ? `https://instagram.com/${talentProfile.instagram_handle}` : "",
      youtube_link: talentProfile?.youtube_channel || "",
      instagram_followers: talentProfile?.followers_count?.toString() || "",
      youtube_followers: ""
    });
    setShowVerifyModal(true);
  };
  
  const handleVerifyPayment = async () => {
    // Validate at least one platform
    if (!verifyForm.instagram_link && !verifyForm.youtube_link) {
      toast.error("Please add at least one social media link");
      return;
    }
    
    if (verifyForm.instagram_link && !verifyForm.instagram_followers) {
      toast.error("Please enter your Instagram followers count");
      return;
    }
    
    if (verifyForm.youtube_link && !verifyForm.youtube_followers) {
      toast.error("Please enter your YouTube subscribers count");
      return;
    }
    
    setVerifyLoading(true);
    
    try {
      // Step 1: Create Razorpay order (₹199 = 19900 paise)
      const orderResponse = await axios.post(`${API}/payment/create-order`, {
        amount: 19900, // ₹199 in paise
        purpose: "verification"
      }, { headers: getAuthHeaders() });
      
      const { order_id, amount, currency, key_id } = orderResponse.data;
      
      // Step 2: Open Razorpay checkout
      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: "FOUNDSTAR",
          description: "Pro Creator Badge - ₹199",
        order_id: order_id,
        handler: async function (response) {
          try {
            // Step 3: Verify payment
            const verifyResponse = await axios.post(`${API}/payment/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            }, { headers: getAuthHeaders() });
            
            if (verifyResponse.data.success) {
              // Step 4: Submit verification request
              const instagramFollowers = parseInt(verifyForm.instagram_followers) || 0;
              const youtubeFollowers = parseInt(verifyForm.youtube_followers) || 0;
              const totalFollowers = instagramFollowers + youtubeFollowers;
              
              try {
                await axios.post(`${API}/influencer/verification-request`, {
                  name: user?.name || profile?.name,
                  email: user?.email || profile?.email,
                  platforms: [
                    ...(verifyForm.instagram_link ? ["Instagram"] : []),
                    ...(verifyForm.youtube_link ? ["YouTube"] : [])
                  ],
                  platform: verifyForm.instagram_link ? "Instagram" : "YouTube",
                  followers_count: totalFollowers,
                  instagram_followers: instagramFollowers,
                  youtube_followers: youtubeFollowers,
                  instagram_link: verifyForm.instagram_link || null,
                  youtube_link: verifyForm.youtube_link || null,
                  platform_link: verifyForm.instagram_link || verifyForm.youtube_link,
                  payment_id: response.razorpay_payment_id,
                  order_id: response.razorpay_order_id
                }, { headers: getAuthHeaders() });
                
                toast.success("Payment successful! Verification request submitted. Admin will review within 24-48 hours.");
                setShowVerifyModal(false);
                fetchProfile();
                fetchTalentProfile();
              } catch (verificationError) {
                console.error("Verification request error:", verificationError);
                toast.error("Payment was successful but verification request failed. Please contact support with Payment ID: " + response.razorpay_payment_id);
              }
            } else {
              toast.error("Payment verification returned unsuccessful. Contact support.");
            }
          } catch (error) {
            console.error("Payment verification error:", error);
            toast.error("Payment verification failed: " + (error.response?.data?.detail || "Please contact support"));
          }
          setVerifyLoading(false);
        },
        prefill: {
          name: user?.name || "",
          email: user?.email || ""
        },
        theme: {
          color: "#7B2FBE"
        },
        modal: {
          ondismiss: function() {
            setVerifyLoading(false);
            toast.error("Payment cancelled");
          },
          confirm_close: true,
          escape: false
        },
        notes: {
          purpose: "verified_badge"
        }
      };
      
      const razorpay = new window.Razorpay(options);
      setTimeout(() => {
        razorpay.open();
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
      setVerifyLoading(false);
    }
  };
  
  // Calculate profile strength
  const getProfileStrength = () => {
    const checks = [
      { id: 'account', label: 'Account created', done: true },
      { id: 'talent_profile', label: 'Talent profile created', done: !!talentProfile, action: () => navigate('/dashboard?tab=talent'), actionLabel: 'Create Now' },
      { id: 'photo', label: 'Photo uploaded', done: !!(talentProfile?.photos?.length > 0 || profile?.picture), action: () => navigate('/dashboard?tab=talent'), actionLabel: 'Add Photo' },
      { id: 'rate', label: 'Rate set', done: !!(talentProfile?.hourly_rate), action: () => navigate('/dashboard?tab=talent'), actionLabel: 'Set Rate' },
      { id: 'follow_instagram', label: (<>Follow <a href={FOUNDSTAR_IG_URL} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#7B2FBE] underline font-semibold">@{FOUNDSTAR_IG_HANDLE}</a> on Instagram</>), done: !!profile?.followed_instagram, action: handleFollowInstagramTask, actionLabel: 'Follow', socialTask: true },
      { id: 'verified', label: 'Pro Creator badge', done: !!(talentProfile?.is_verified || profile?.is_verified), action: handleGetVerified, actionLabel: 'Become Pro — ₹199', special: true }
    ];
    const completed = checks.filter(c => c.done).length;
    const percentage = (completed / checks.length) * 100;
    return { checks, completed, total: checks.length, percentage };
  };
  
  const profileStrength = getProfileStrength();
  const unreadNotifications = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF69B4]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Top Greeting Bar */}
      <header className="bg-gradient-to-r from-[#FF69B4] to-[#7B2FBE] text-white p-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="w-12 h-12 border-2 border-white/30">
              <AvatarImage src={user?.picture || profile?.picture} />
              <AvatarFallback className="bg-white/20 text-white text-lg">{user?.name?.[0]}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold text-lg">Hi {user?.name?.split(' ')[0]}! 👋</p>
              <p className="text-white/80 text-sm">Ready to get hired today?</p>
            </div>
          </div>
          
          <button 
            onClick={() => navigate('/dashboard')}
            className="relative p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <Bell className="w-6 h-6" />
            {unreadNotifications > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {unreadNotifications > 9 ? '9+' : unreadNotifications}
              </span>
            )}
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 space-y-6">
        {/* SECTION 1 — Profile Incomplete Banner */}
        {!talentProfile && (
          <Card className="bg-[#FF6B00] text-white overflow-hidden">
            <CardContent className="p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Zap className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-semibold">Brands can't find you yet!</p>
                  <p className="text-white/80 text-sm">Create your Talent Profile now.</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/dashboard?tab=talent')}
                className="bg-white text-[#FF6B00] hover:bg-white/90 rounded-full px-5 whitespace-nowrap"
              >
                Create Profile
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* SECTION 3 — Live Opportunities */}
        <div>
          <h2 className="font-semibold text-lg text-[#1E293B] mb-4 flex items-center gap-2">
            <span className="text-xl">🔥</span> Open Opportunities For You
          </h2>
          
          <div className="space-y-3">
            {opportunities.length === 0 ? (
              <Card className="bg-gradient-to-r from-purple-50 to-pink-50">
                <CardContent className="p-6 text-center">
                  <p className="text-gray-500 mb-2">No opportunities available right now</p>
                  <p className="text-sm text-gray-400">Check back soon for new gigs!</p>
                </CardContent>
              </Card>
            ) : (
              opportunities.map((opp) => {
                const catConfig = categoryIcons[opp.category] || categoryIcons.influencers;
                const daysLeft = opp.deadline_days || 7;
                
                return (
                  <Card 
                    key={opp.opportunity_id} 
                    className={`bg-white hover:shadow-md transition-all border-l-4 ${
                      !opp.is_eligible ? 'opacity-70' : ''
                    }`} 
                    style={{ borderLeftColor: catConfig.color }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="text-lg">{catConfig.emoji}</span>
                            <Badge style={{ backgroundColor: catConfig.color }} className="text-white text-xs">
                              {opp.platform}
                            </Badge>
                            {opp.verified_only && (
                              <Badge className="bg-yellow-500 text-white text-xs">Verified Only</Badge>
                            )}
                            <Badge className="bg-[#00A86B] text-white text-xs">
                              {opp.created_by || "FOUNDSTAR"}
                            </Badge>
                          </div>
                          
                          <p className="font-medium text-[#1E293B] mb-1">{opp.title}</p>
                          <p className="text-sm text-gray-500 mb-2">{opp.description}</p>
                          
                          <div className="flex items-center gap-4 text-sm flex-wrap">
                            <span className="flex items-center gap-1 font-bold text-[#00A86B] text-lg">
                              <IndianRupee className="w-4 h-4" />
                              {opp.payment_amount}
                              <span className="text-xs font-normal text-gray-500">/{opp.payment_type?.replace('_', ' ')}</span>
                            </span>
                            <span className="flex items-center gap-1 text-gray-500">
                              <Clock className="w-4 h-4" />
                              {daysLeft} days to complete
                            </span>
                            <span className="text-gray-400 text-xs">
                              {opp.applications_count || 0}/{opp.max_applications} applied
                            </span>
                          </div>
                          
                          {!opp.is_eligible && opp.eligibility_reason && (
                            <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                              <XCircle className="w-3 h-3" />
                              {opp.eligibility_reason}
                            </p>
                          )}
                        </div>
                        
                        <div className="shrink-0">
                          {opp.has_applied ? (
                            <Badge 
                              className={`${
                                opp.application_status === 'approved' ? 'bg-green-500' :
                                opp.application_status === 'rejected' ? 'bg-red-500' :
                                opp.application_status === 'completed' ? 'bg-blue-500' :
                                'bg-yellow-500'
                              } text-white`}
                            >
                              {opp.application_status === 'approved' ? '✓ Approved' :
                               opp.application_status === 'rejected' ? '✗ Rejected' :
                               opp.application_status === 'completed' ? '✓ Completed' :
                               '⏳ Applied'}
                            </Badge>
                          ) : (
                            <Button 
                              onClick={() => applyToOpportunity(opp.opportunity_id)}
                              disabled={!opp.is_eligible}
                              className={`${
                                opp.is_eligible 
                                  ? 'bg-[#FF69B4] hover:bg-[#FF69B4]/90' 
                                  : 'bg-gray-300 cursor-not-allowed'
                              } text-white rounded-full px-4`}
                              size="sm"
                            >
                              Apply Now
                              <ArrowRight className="w-4 h-4 ml-1" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </div>

        {/* SECTION 3.5 — Stars of the Day (premium animated showcase) */}
        <StarsOfTheDaySection stars={starsOfDay} currentUser={user} />

        {/* SECTION 4 — Profile Strength */}
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg text-[#1E293B] flex items-center gap-2">
                <span className="text-xl">💪</span> Strengthen Your Profile
              </h3>
              <span className="text-sm font-medium text-[#7B2FBE]">
                {profileStrength.completed}/{profileStrength.total} complete
              </span>
            </div>
            
            <Progress 
              value={profileStrength.percentage} 
              className="h-2 mb-4"
            />
            
            <div className="space-y-3">
              {profileStrength.checks.map((check) => (
                <div 
                  key={check.id} 
                  className={`flex items-center justify-between p-3 rounded-xl transition-all ${
                    check.done
                      ? 'bg-green-50'
                      : check.special
                      ? 'bg-purple-50'
                      : check.socialTask
                      ? 'bg-gradient-to-r from-pink-50 via-fuchsia-50 to-orange-50'
                      : 'bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {check.done ? (
                      <div className="w-6 h-6 bg-[#00A86B] rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    ) : check.socialTask ? (
                      <div className="w-6 h-6 bg-gradient-to-tr from-pink-500 via-fuchsia-500 to-orange-400 rounded-full flex items-center justify-center text-white text-[11px] font-bold">
                        IG
                      </div>
                    ) : (
                      <div className="w-6 h-6 border-2 border-gray-300 rounded-full flex items-center justify-center">
                        <Circle className="w-3 h-3 text-gray-300" />
                      </div>
                    )}
                    <span className={`text-sm ${check.done ? 'text-[#00A86B] font-medium' : 'text-gray-600'}`}>
                      {check.label}
                    </span>
                  </div>
                  
                  {!check.done && check.action && (
                    <Button 
                      onClick={check.action}
                      size="sm"
                      className={check.special 
                        ? 'bg-[#7B2FBE] hover:bg-[#6B25A8] text-white rounded-full text-xs'
                        : check.socialTask
                        ? 'bg-gradient-to-r from-pink-500 via-fuchsia-500 to-orange-400 hover:opacity-90 text-white rounded-full text-xs px-3'
                        : 'bg-[#FF69B4] hover:bg-[#FF69B4]/90 text-white rounded-full text-xs'
                      }
                    >
                      {check.actionLabel}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* SECTION 5 — Refer & Earn */}
        <Card className="bg-gradient-to-r from-[#00A86B] to-[#34D399] text-white overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-semibold flex items-center gap-2">
                    <span>👥</span> Refer a friend — Earn ₹20
                  </p>
                  <p className="text-white/80 text-sm">They get ₹10 bonus too!</p>
                </div>
              </div>
              
              <Button 
                onClick={shareOnWhatsApp}
                className="bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-full px-5"
              >
                <Share2 className="w-4 h-4 mr-1" />
                Share on WhatsApp
              </Button>
            </div>
            
            {referralInfo && (
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/20 text-sm">
                <div className="bg-white/20 rounded-lg px-3 py-2">
                  <p className="text-white/70 text-xs">Referred</p>
                  <p className="font-bold">{referralInfo.total_referrals || 0}</p>
                </div>
                <div className="bg-white/20 rounded-lg px-3 py-2">
                  <p className="text-white/70 text-xs">Earned</p>
                  <p className="font-bold">₹{referralInfo.total_earnings || 0}</p>
                </div>
                <div className="bg-white/20 rounded-lg px-3 py-2 flex-1">
                  <p className="text-white/70 text-xs">Your Code</p>
                  <p className="font-bold">{referralInfo.referral_code}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4">
          <Button 
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="border-[#FF69B4] text-[#FF69B4] hover:bg-pink-50 py-6 rounded-2xl"
          >
            <TrendingUp className="w-5 h-5 mr-2" />
            View Dashboard
          </Button>
          <Button 
            onClick={() => navigate('/dashboard?tab=talent')}
            className="bg-gradient-to-r from-[#FF69B4] to-[#7B2FBE] text-white py-6 rounded-2xl"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            {talentProfile ? 'Edit Profile' : 'Create Profile'}
          </Button>
        </div>
      </main>

      {/* Verified Badge Modal */}
      <Dialog open={showVerifyModal} onOpenChange={setShowVerifyModal}>
        <DialogContent className="sm:max-w-md">
          <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#7B2FBE] to-[#FF69B4] rounded-t-lg"></div>
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl text-center flex items-center justify-center gap-2">
              <BadgeCheck className="w-6 h-6 text-[#7B2FBE]" />
              Become a Pro Creator
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Benefits */}
            <div className="bg-purple-50 rounded-xl p-4 space-y-2">
              <p className="font-medium text-[#7B2FBE] text-sm">Benefits of Pro Creator Badge:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#00A86B]" />
                  Stand out with Pro Creator badge
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#00A86B]" />
                  Priority in search results
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#00A86B]" />
                  Build trust with brands
                </li>
              </ul>
            </div>
            
            {/* Form */}
            <div className="space-y-3">
              <div>
                <Label className="text-sm">Instagram Profile URL</Label>
                <Input
                  placeholder="https://instagram.com/yourhandle"
                  value={verifyForm.instagram_link}
                  onChange={(e) => setVerifyForm({ ...verifyForm, instagram_link: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              {verifyForm.instagram_link && (
                <div>
                  <Label className="text-sm">Instagram Followers</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 10000"
                    value={verifyForm.instagram_followers}
                    onChange={(e) => setVerifyForm({ ...verifyForm, instagram_followers: e.target.value })}
                    className="mt-1"
                  />
                </div>
              )}
              
              <div>
                <Label className="text-sm">YouTube Channel URL (Optional)</Label>
                <Input
                  placeholder="https://youtube.com/@yourchannel"
                  value={verifyForm.youtube_link}
                  onChange={(e) => setVerifyForm({ ...verifyForm, youtube_link: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              {verifyForm.youtube_link && (
                <div>
                  <Label className="text-sm">YouTube Subscribers</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 5000"
                    value={verifyForm.youtube_followers}
                    onChange={(e) => setVerifyForm({ ...verifyForm, youtube_followers: e.target.value })}
                    className="mt-1"
                  />
                </div>
              )}
            </div>
            
            {/* Price */}
            <div className="bg-gradient-to-r from-[#7B2FBE] to-[#FF69B4] rounded-xl p-4 text-white text-center">
              <p className="text-white/80 text-sm">One-time fee</p>
              <p className="text-3xl font-bold">₹199</p>
              <p className="text-white/60 text-xs mt-1">Secure payment via Razorpay</p>
            </div>
            
            <Button
              onClick={handleVerifyPayment}
              disabled={verifyLoading}
              className="w-full bg-gradient-to-r from-[#7B2FBE] to-[#FF69B4] hover:opacity-90 text-white py-6 rounded-full text-lg font-semibold"
            >
              {verifyLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <BadgeCheck className="w-5 h-5 mr-2" />
                  Pay ₹199 & Become Pro
                </>
              )}
            </Button>
            
            <p className="text-xs text-gray-400 text-center">
              Admin will review your profile within 24-48 hours after payment
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <BottomNav userType="influencer" />
    </div>
  );
};

export default TalentHome;
